import React, { useMemo } from 'react';
import { ColDef, ICellRendererParams, RowDragEvent } from 'ag-grid-community';
import { ApolloError } from 'apollo-client';
import AdminList, { NameCellRenderer } from '../../../../ui/admin/AdminList';
import AdminListToolbar from '../../../../ui/admin/AdminListToolbar';
import Footer from '../../ChannelGroupList/components/Footer';
import { GroupedUserGuideScopeRow } from '../types';
import { name, title, apiName } from '../constants';

interface UserGuideScopesTableProps {
    items: GroupedUserGuideScopeRow[];
    loading: boolean;
    error?: ApolloError;
    searchString: string;
    onQueryStringChange: (value: string) => void;
    onRowDragEnd: (event: RowDragEvent) => void;
    hasPendingChanges: boolean;
    saving: boolean;
    onSaveOrder: () => Promise<void>;
}

const UserGuideScopesTable: React.FC<UserGuideScopesTableProps> = ({
    items,
    loading,
    error,
    searchString,
    onQueryStringChange,
    onRowDragEnd,
    hasPendingChanges,
    saving,
    onSaveOrder,
}) => {
    const canReorder = !searchString.trim();
    const columnDefs = useMemo<ColDef<GroupedUserGuideScopeRow>[]>(
        () => [
            {
                colId: 'user-guide-scope-order-drag-col',
                headerName: '',
                width: 40,
                rowDrag: canReorder,
                cellRenderer: 'agRowDragCellRenderer',
                suppressHeaderMenuButton: true,
                sortable: false,
                menuTabs: [],
            },
            {
                colId: 'user-guide-scope-name-col',
                headerName: 'Name',
                field: 'name',
                cellRenderer: (
                    props: ICellRendererParams<GroupedUserGuideScopeRow>
                ): JSX.Element => (
                    <NameCellRenderer {...props} apiName={apiName} />
                ),
                width: 280,
                menuTabs: [],
            },
            {
                colId: 'user-guide-scope-scope-col',
                headerName: 'Scope',
                field: 'scope',
                width: 420,
                menuTabs: [],
            },
        ],
        [canReorder]
    );

    return (
        <AdminList
            name={name}
            title={title}
            items={items}
            loading={loading}
            error={error}
            columns={columnDefs}
            showAdd={true}
            addButtonLabel="Add scope"
            entityForCaptionText="scope"
            pagination={false}
            gridOptions={{
                pagination: false,
                rowDragManaged: canReorder,
                animateRows: true,
                onRowDragEnd: canReorder ? onRowDragEnd : undefined,
            }}
            footer={
                <Footer
                    wrapperSx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                    }}
                    disabled={!canReorder || !hasPendingChanges || saving}
                    onSave={onSaveOrder}
                    buttonId="save-user-guide-scope-order-button"
                />
            }
            toolbar={
                <AdminListToolbar
                    searchString={searchString}
                    toolbarPlaceholder="Search user guide scopes..."
                    onQueryStringChange={onQueryStringChange}
                />
            }
        />
    );
};

export default UserGuideScopesTable;
